import type { CSSProperties, ReactNode } from 'react';
import { useLocale } from '../../lib/i18n';
import { sx } from './styles';
import { SENTIMENT_META } from '../../lib/sentiment';

// Read-only reference panel: documents HOW conversations auto-resolve.
// Mirrors the backend rules in src/services/chats.ts (decideAutoResolve +
// AUTO_RESOLVE_MESSAGES). Keep the copy here in sync if those change.

// The exact customer-facing closing messages (verbatim from the backend).
const CLOSING = {
  silence: {
    he: 'לא שמענו ממך זמן מה, אז אנחנו סוגרים את השיחה. אפשר להשיב בכל עת כדי לפתוח אותה מחדש.',
    en: "We haven't heard from you for a while, so we're closing this conversation. Reply anytime to reopen it.",
  },
  stale: {
    he: 'תודה שפנית אלינו — האם עדיין נדרשת עזרה? השב/י כדי לפתוח מחדש.',
    en: 'Thank you for contacting us — do you still require assistance? Reply to reopen.',
  },
};

const COPY = {
  en: {
    title: 'Auto-resolve — how conversations close automatically',
    intro:
      'A background sweep runs every 15 minutes. For each ACTIVE conversation (New / Open / In progress) it applies these rules top-to-bottom and stops at the first match. Resolved and snoozed conversations are never touched.',
    treeTitle: 'Decision tree',
    start: 'Active conversation (New / Open / In progress)',
    q1: 'Escalation with NO human reply yet?',
    q1yes7d: 'idle ≥ 7 days → RESOLVE (abandoned)',
    q1elsekeep: 'otherwise → keep open (protected — only a human reply or 7 days closes it)',
    q2: 'We (bot or agent) spoke last AND the customer has been silent ≥ 23 hours?',
    q2yes: 'RESOLVE',
    q3: 'Anything else idle ≥ 7 days?',
    q3yes: 'RESOLVE',
    none: 'No rule matched → stays open',
    msgTitle: 'Closing messages',
    msgSilence: '23h silence',
    msgStale: '7-day / abandoned escalation',
    notesTitle: 'Notes',
    notes: [
      'Why 23h and not 24h: the 23h notice lands INSIDE WhatsApp’s 24-hour service window, so it can be delivered free-form on every channel.',
      'WhatsApp + the 7-day message: it fires after the window closes, so WhatsApp blocks it — that conversation resolves silently on WhatsApp (web and email still deliver the message).',
      'Escalation protection: a conversation that asked for (or was routed to) a human is protected from the 23h rule until a human actually replies. After a human reply, the 23h rule applies normally.',
      'Reopen: if the customer replies after auto-close, the conversation reopens automatically as a fresh episode — the closing message stays in the prior thread.',
      'Hand back to AI: when an agent hands a chat back to the AI, it clears the assignment and escalation flags, so it moves from “In progress” back to “New”.',
    ],
    reasonsTitle: 'Resolution reasons (audit)',
    reasons: [
      ['abandoned_escalation', 'Escalated, no human reply, idle ≥ 7 days'],
      ['customer_silence_23h', 'We spoke last, customer silent ≥ 23 hours'],
      ['stale_7d', 'Catch-all: idle ≥ 7 days'],
    ],
    sentimentTitle: 'Customer sentiment',
    sentimentIntro:
      'The AI reads the customer’s mood on every inbound message and shows it in the conversation header as “Beginning → Now”. Beginning is the first turn of the current episode; Now is the latest. It keeps updating every customer turn — including while a human agent has taken the chat. The face runs on a green → yellow → orange → red scale.',
    sentimentRows: [
      ['positive', 'Positive', 'Happy, satisfied, or thankful'],
      ['neutral', 'Neutral', 'Factual — no strong emotion'],
      ['sarcastic', 'Sarcastic', 'Insincere praise — a wrapped complaint'],
      ['negative', 'Negative', 'Frustrated, angry, or disappointed'],
    ],
  },
  he: {
    title: 'סגירה אוטומטית — איך שיחות נסגרות מעצמן',
    intro:
      'תהליך רקע רץ כל 15 דקות. עבור כל שיחה פעילה (חדש / פתוח / בטיפול) הוא בודק את הכללים מלמעלה למטה ועוצר בכלל הראשון שמתקיים. שיחות שנפתרו או נדחו (snoozed) אינן מושפעות.',
    treeTitle: 'עץ החלטה',
    start: 'שיחה פעילה (חדש / פתוח / בטיפול)',
    q1: 'הסלמה ללא תגובת נציג אנושי עדיין?',
    q1yes7d: 'ללא פעילות ≥ 7 ימים → סגירה (ננטשה)',
    q1elsekeep: 'אחרת → נשארת פתוחה (מוגנת — רק תגובת נציג או 7 ימים יסגרו אותה)',
    q2: 'אנחנו (בוט או נציג) הגבנו אחרונים והלקוח שותק ≥ 23 שעות?',
    q2yes: 'סגירה',
    q3: 'כל מקרה אחר ללא פעילות ≥ 7 ימים?',
    q3yes: 'סגירה',
    none: 'אף כלל לא התקיים → נשארת פתוחה',
    msgTitle: 'הודעות סגירה',
    msgSilence: 'שתיקה של 23 שעות',
    msgStale: '7 ימים / הסלמה שננטשה',
    notesTitle: 'הערות',
    notes: [
      'למה 23 שעות ולא 24: ההודעה ב-23 שעות נכנסת בתוך חלון השירות של 24 שעות בוואטסאפ, כך שניתן לשלוח אותה בטקסט חופשי בכל הערוצים.',
      'וואטסאפ + הודעת 7 הימים: היא נשלחת אחרי סגירת החלון, ולכן וואטסאפ חוסם אותה — השיחה נסגרת בשקט בוואטסאפ (בצ׳אט באתר ובמייל ההודעה כן נשלחת).',
      'הגנת הסלמה: שיחה שביקשה (או נותבה ל-) נציג אנושי מוגנת מכלל ה-23 שעות עד שנציג באמת מגיב. לאחר תגובת נציג, כלל ה-23 שעות חל כרגיל.',
      'פתיחה מחדש: אם הלקוח מגיב אחרי הסגירה האוטומטית, השיחה נפתחת מחדש כפרק חדש — הודעת הסגירה נשארת בשרשור הקודם.',
      'החזרה ל-AI: כשנציג מחזיר שיחה ל-AI, ההקצאה ודגלי ההסלמה מתנקים, כך שהיא חוזרת מ״בטיפול״ ל״חדש״.',
    ],
    reasonsTitle: 'סיבות סגירה (ביקורת)',
    reasons: [
      ['abandoned_escalation', 'הוסלמה, ללא תגובת נציג, ללא פעילות ≥ 7 ימים'],
      ['customer_silence_23h', 'הגבנו אחרונים, הלקוח שותק ≥ 23 שעות'],
      ['stale_7d', 'כלל גורף: ללא פעילות ≥ 7 ימים'],
    ],
    sentimentTitle: 'סנטימנט הלקוח',
    sentimentIntro:
      'ה-AI מזהה את מצב הרוח של הלקוח בכל הודעה נכנסת ומציג אותו בכותרת השיחה כ״התחלה → עכשיו״. ״התחלה״ היא הפנייה הראשונה בפרק הנוכחי; ״עכשיו״ היא האחרונה. הוא מתעדכן בכל תור של הלקוח — גם כשנציג אנושי לקח את השיחה. הפנים נעות על סקלה ירוק → צהוב → כתום → אדום.',
    sentimentRows: [
      ['positive', 'חיובי', 'מרוצה, שמח או מודה'],
      ['neutral', 'ניטרלי', 'ענייני — ללא רגש חזק'],
      ['sarcastic', 'סרקסטי', 'מחמאה לא כנה — תלונה עטופה'],
      ['negative', 'שלילי', 'מתוסכל, כועס או מאוכזב'],
    ],
  },
};

function Node({ children, tone = 'neutral' }: { children: ReactNode; tone?: 'neutral' | 'q' | 'resolve' | 'keep' }) {
  const tones: Record<string, CSSProperties> = {
    neutral: { background: '#f3f4f6', border: '1px solid #e5e7eb', color: '#374151' },
    q: { background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e40af' },
    resolve: { background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c' },
    keep: { background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#15803d' },
  };
  return (
    <div style={{ ...tones[tone], borderRadius: 8, padding: '8px 12px', fontSize: 13, fontWeight: 500, display: 'inline-block' }}>
      {children}
    </div>
  );
}

function Branch({ children }: { children: ReactNode }) {
  return (
    <div style={{ marginInlineStart: 18, paddingInlineStart: 14, borderInlineStart: '2px solid #e5e7eb', marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start' }}>
      {children}
    </div>
  );
}

export function InfoPanel() {
  const { locale } = useLocale();
  const c = COPY[locale] ?? COPY.en;

  return (
    <div style={{ maxWidth: 760 }}>
      <h1 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>{c.title}</h1>
      <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 20, lineHeight: 1.6 }}>{c.intro}</p>

      {/* Decision tree */}
      <div style={sx.card}>
        <div style={sx.label}>{c.treeTitle}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start', marginTop: 8 }}>
          <Node tone="neutral">{c.start}</Node>
          <Branch>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={sx.badge('#2563eb')}>1</span>
              <Node tone="q">{c.q1}</Node>
            </div>
            <Branch>
              <Node tone="resolve">{c.q1yes7d}</Node>
              <Node tone="keep">{c.q1elsekeep}</Node>
            </Branch>

            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={sx.badge('#2563eb')}>2</span>
              <Node tone="q">{c.q2}</Node>
            </div>
            <Branch>
              <Node tone="resolve">{c.q2yes} → “{CLOSING.silence[locale]}”</Node>
            </Branch>

            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={sx.badge('#2563eb')}>3</span>
              <Node tone="q">{c.q3}</Node>
            </div>
            <Branch>
              <Node tone="resolve">{c.q3yes} → “{CLOSING.stale[locale]}”</Node>
            </Branch>

            <Node tone="keep">{c.none}</Node>
          </Branch>
        </div>
      </div>

      {/* Closing messages */}
      <div style={sx.card}>
        <div style={sx.label}>{c.msgTitle}</div>
        <table style={{ ...sx.table, marginTop: 8 }}>
          <tbody>
            <tr>
              <td style={{ ...sx.td, width: 180, verticalAlign: 'top', fontWeight: 600 }}>{c.msgSilence}</td>
              <td style={sx.td}>
                <div dir="rtl">{CLOSING.silence.he}</div>
                <div dir="ltr" style={{ color: '#6b7280', marginTop: 4 }}>{CLOSING.silence.en}</div>
              </td>
            </tr>
            <tr>
              <td style={{ ...sx.td, verticalAlign: 'top', fontWeight: 600 }}>{c.msgStale}</td>
              <td style={sx.td}>
                <div dir="rtl">{CLOSING.stale.he}</div>
                <div dir="ltr" style={{ color: '#6b7280', marginTop: 4 }}>{CLOSING.stale.en}</div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Resolution reasons */}
      <div style={sx.card}>
        <div style={sx.label}>{c.reasonsTitle}</div>
        <table style={{ ...sx.table, marginTop: 8 }}>
          <tbody>
            {c.reasons.map(([code, desc]) => (
              <tr key={code}>
                <td style={{ ...sx.td, width: 200 }}>
                  <code style={{ fontSize: 12, background: '#f3f4f6', padding: '2px 6px', borderRadius: 4 }}>{code}</code>
                </td>
                <td style={sx.td}>{desc}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Notes */}
      <div style={sx.card}>
        <div style={sx.label}>{c.notesTitle}</div>
        <ul style={{ margin: '8px 0 0', paddingInlineStart: 18, fontSize: 13, color: '#374151', lineHeight: 1.7 }}>
          {c.notes.map((n, i) => (
            <li key={i}>{n}</li>
          ))}
        </ul>
      </div>

      {/* Customer sentiment legend */}
      <div style={sx.card}>
        <div style={sx.label}>{c.sentimentTitle}</div>
        <p style={{ fontSize: 13, color: '#6b7280', margin: '8px 0 12px', lineHeight: 1.6 }}>{c.sentimentIntro}</p>
        <table style={{ ...sx.table }}>
          <tbody>
            {c.sentimentRows.map(([key, label, desc]) => {
              const m = SENTIMENT_META[key];
              if (!m) return null;
              const Icon = m.Icon;
              return (
                <tr key={key}>
                  <td style={{ ...sx.td, width: 40 }}>
                    <Icon size={18} style={{ color: m.color }} />
                  </td>
                  <td style={{ ...sx.td, width: 120, fontWeight: 600, color: m.color }}>{label}</td>
                  <td style={sx.td}>{desc}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
